import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { makeStyles, lighten } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import XGrid from "src/components/CSVFormattedXGrid";
import {
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
} from "@material-ui/x-grid";

import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import { valueGetterWithDefault } from "src/utils/valueGetter";
import { formatCurrency, formatAbsCurrency, formatProfitCurrency } from "src/utils/formatter";
import makeRenderCell from "src/utils/makeRenderCell";
import vendorRenderCell from "src/utils/vendorRenderCell";
import tooltipRenderCell from "src/utils/tooltipRenderCell";
import { userGetInventoryTypeSelector } from "src/store/system/selector";

import {
  makeSalesStyle,
  makeTransactionsStyle,
  makeProfitStyle,
} from "src/utils/gridCellStyles";
import type { ConsignmentCommissionReport as ConsignmentCommissionReportType } from "./report";
import Title, { DownloadButton } from "./Title";
import CompanyInfo from "./CompanyInfo";
import { render as pdfRender } from "./pdf";

interface ConsignmentCommissionReportProps {
  report: ConsignmentCommissionReportType;
}

const useValueBoxStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const defaultColor = isDark
    ? theme.palette.common.white
    : theme.palette.common.black;
  const successColor = isDark
    ? lighten(theme.palette.success.main, 0.2)
    : theme.palette.success.main;

  return {
    box: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      minWidth: "15%",
    },
    title: {
      color: theme.palette.text.primary,
    },
    children: {
      textAlign: "right",
    },
    default: {
      color: defaultColor,
    },
    greenPositive: {
      color: successColor,
    },
    profitPositive: {
      color: successColor,
    },
    profitNegative: {
      color: theme.palette.error.main,
    },
  };
});

type ValueBoxColorType =
  | "sales"
  | "refunds"
  | "expenses"
  | "profit"
  | "commission";

function ValueBox({
  title,
  children,
  colorType,
}: {
  title: string;
  children: number;
  colorType?: ValueBoxColorType;
}) {
  const classes = useValueBoxStyles();

  const getAmountClass = () => {
    if (!colorType) return classes.default;

    switch (colorType) {
      case "sales":
        // Green for positive sales
        return children > 0 ? classes.greenPositive : classes.default;
      case "refunds":
        // Green for positive refunds (reversed)
        return children > 0 ? classes.greenPositive : classes.default;
      case "expenses":
        // Green for negative expenses (they reduce costs)
        return children < 0 ? classes.greenPositive : classes.default;
      case "profit":
        // Green for positive, red for negative
        if (children > 0) return classes.profitPositive;
        if (children < 0) return classes.profitNegative;
        return classes.default;
      case "commission":
        // Green for positive, red for negative
        if (children > 0) return classes.greenPositive;
        if (children < 0) return classes.profitNegative;
        return classes.default;
      default:
        return classes.default;
    }
  };

  return (
    <Paper className={classes.box} variant="outlined">
      <Typography
        variant="button"
        component="p"
        display="block"
        className={classes.title}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        className={`${classes.children} ${getAmountClass()}`}
      >
        {new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(colorType === "refunds" ? Math.abs(children) : children)}
      </Typography>
    </Paper>
  );
}

const Columns = [
  {
    field: "transaction_id",
    headerName: "Platform ID",
    width: 150,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "item_title",
    headerName: "Item Title",
    minWidth: 200,
    flex: 1,
    renderCell: tooltipRenderCell,
  },
  {
    field: "sale_date",
    headerName: "Sale Date",
    type: "date",
    width: 110,
  },
  {
    field: "sale_price",
    headerName: "Sale Price",
    type: "number",
    width: 145,
    renderCell: makeRenderCell({ valueFormatter: formatAbsCurrency, style: makeSalesStyle }),
  },
  {
    field: "purchase_price",
    headerName: "Purchase Price",
    type: "number",
    width: 145,
    renderCell: makeRenderCell({ valueFormatter: formatAbsCurrency, style: makeTransactionsStyle }),
  },
  {
    field: "shipping_cost",
    headerName: "Shipping Costs",
    type: "number",
    width: 145,
    renderCell: makeRenderCell({ valueFormatter: formatAbsCurrency, style: makeTransactionsStyle }),
  },
  {
    field: "shipping_cost_analytics",
    headerName: "Shipping Costs (From Expense Detail)",
    type: "number",
    width: 265,
    renderCell: makeRenderCell({ valueFormatter: formatAbsCurrency, style: makeTransactionsStyle }),
  },
  {
    field: "transaction_fees",
    headerName: "Transaction Fees",
    type: "number",
    width: 155,
    renderCell: makeRenderCell({ valueFormatter: formatAbsCurrency, style: makeTransactionsStyle }),
  },
  {
    field: "other_fees",
    headerName: "Other Costs (From Expense Detail)",
    type: "number",
    width: 250,
    renderCell: makeRenderCell({ valueFormatter: formatAbsCurrency, style: makeTransactionsStyle }),
  },
  {
    field: "gross_profit",
    headerName: "Gross profit",
    type: "number",
    width: 125,
    renderCell: makeRenderCell({ valueFormatter: formatProfitCurrency, style: makeProfitStyle }),
  },
  {
    field: "sale_platform",
    headerName: "Sale Platform",
    width: 140,
    renderCell: tooltipRenderCell,
  },
  {
    field: "return_date",
    headerName: "Return Date",
    type: "date",
    width: 125,
  },
  {
    field: "extra_shipping_cost",
    headerName: "Return Shipping Cost",
    type: "number",
    width: 175,
    renderCell: makeRenderCell({ valueFormatter: formatAbsCurrency, style: makeTransactionsStyle }),
  },
  {
    field: "extra_transaction_fees",
    headerName: "Return Transaction Fees",
    type: "number",
    width: 195,
    renderCell: makeRenderCell({ valueFormatter: formatAbsCurrency, style: makeTransactionsStyle }),
  },
  {
    field: "vendor",
    headerName: "Vendor",
    width: 130,
    renderCell: vendorRenderCell,
    valueGetter: (params) => params.row.vendorName || params.value || "",
  },
];
const ColumnsCashIgnored = [
  "purchase_date",
  "purchase_price",
  "platforms_listed",
];
const ColumnsCash = Columns.filter(
  ({ field }) => !ColumnsCashIgnored.includes(field)
);

function formatDate({ value }) {
  return value ? new Intl.DateTimeFormat("en-US").format(value) : "";
}
const PrintColumns = [
  { field: "item_title", headerName: "Item Title" },
  {
    field: "sale_date",
    headerName: "Sale Date",
    valueFormatter: formatDate,
  },
  {
    field: "return_date",
    headerName: "Return Date",
    valueFormatter: formatDate,
  },
  {
    field: "gross_profit",
    headerName: "Gross profit",
    valueFormatter: formatCurrency,
  },
];

function Toolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarColumnsButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "0px 10px",
      fontSize: theme.typography.caption.fontSize,
    },
    "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-cell": {
      color: theme.palette.text.primary,
    },
    "& .MuiDataGrid-iconSeparator": {
      opacity: 0,
    },
    "& .MuiDataGrid-columnsContainer": {
      backgroundColor: theme.palette.action.hover,
      borderTop: `1px solid ${theme.palette.divider}`,
      fontSize: theme.typography.caption.fontSize,
    },
    "& .MuiDataGrid-colCell": {
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "0px 10px",
    },
  },
  sectionTitle: {
    color: theme.palette.text.primary,
  },
}));

function PrintTable({ rows }) {
  return (
    <TableContainer className="hidden consignment-commission-report-table">
      <Table>
        <TableHead>
          <TableRow>
            {PrintColumns.map(({ field, headerName }) => (
              <TableCell key={field}>{headerName}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              {PrintColumns.map((c) => {
                let value = row[c.field] || "";
                if (c.valueFormatter) value = c.valueFormatter({ value });
                return <TableCell key={c.field}>{value}</TableCell>;
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ConsignmentCommissionReport({
  report,
}: ConsignmentCommissionReportProps) {
  const classes = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  const inventoryTypeIsCash =
    useSelector(userGetInventoryTypeSelector) === "cash";
  const handleDownloadReport = async () => {
    if (!ref?.current) return;
    const filename = report.data.reportDetails.filename;
    const toolbar = ref.current.querySelector(".MuiDataGrid-root > div");
    if (toolbar) toolbar.setAttribute("data-html2canvas-ignore", "true");
    await pdfRender(ref.current, {
      filename,
      onclone: (doc) => {
        const table = doc.querySelector(".consignment-commission-report-table");
        if (table) table.className = "";
      },
    });
  };

  return (
    <Paper className={`mt-5 p-5 h-auto ${classes.root}`} ref={ref}>
      <CompanyInfo />
      <Title actions={<DownloadButton onClick={handleDownloadReport} />}>
        {report.data.reportDetails.titleFull}
      </Title>
      <Box display="flex" justifyContent="center">
        <ValueBox title="Sales" colorType="sales">
          {report.data.totals.sales}
        </ValueBox>
        <ValueBox title="Refunds" colorType="refunds">
          {report.data.totals.returns}
        </ValueBox>
        <ValueBox title="Expenses" colorType="expenses">
          {report.data.totals.expenses}
        </ValueBox>
        <ValueBox title="Gross Profit" colorType="profit">
          {report.data.totals.grossProfit}
        </ValueBox>
        <ValueBox title="Commission Due" colorType="commission">
          {report.data.totals.commission}
        </ValueBox>
      </Box>
      <Box mt={2}>
        <h2 className={classes.sectionTitle}>Transaction Detail</h2>
        <PrintTable rows={report.data.sales} />
        <Box minHeight="75vh" data-html2canvas-ignore>
          <XGrid
            components={{
              Toolbar,
            }}
            density="compact"
            rows={report.data.sales}
            columns={inventoryTypeIsCash ? ColumnsCash : Columns}
            disableSelectionOnClick
            autoHeight
            pageSize={report.data.sales.length}
            hideFooter
            hideFooterPagination
            disableColumnMenu
          />
        </Box>
      </Box>
    </Paper>
  );
}

export default ConsignmentCommissionReport;
